export type StaffDashboardRole = 'manager' | 'coach' | 'medical' | 'scout';
export type DashboardReportType = 'scouting' | 'performance' | 'medical' | 'training';

export const isDashboardReportType = (value: unknown): value is DashboardReportType =>
  value === 'scouting' ||
  value === 'performance' ||
  value === 'medical' ||
  value === 'training';

export interface ManagerDashboardStats {
  totalPlayers: number;
  activePlayers: number;
  injuredPlayers: number;
  recentReports: number;
  recentReportFeed: DashboardReportFeedItem[];
  topPerformers: TopPerformer[];
  recentInjuries: DashboardInjury[];
  teamHealth: TeamHealthMetrics;
  performance: PerformanceMetrics;
  upcomingFixtures: UpcomingFixture[];
  criticalAlerts: Alert[];
  trainingMetrics: TrainingMetrics;
}

export interface CoachDashboardStats extends ManagerDashboardStats {}

export interface MedicalDashboardStats {
  totalPlayers: number;
  injuredPlayers: number;
  recoveryPlayers: number;
  fitnessAverage: number;
  upcomingFixtures: UpcomingFixture[];
  criticalAlerts: Alert[];
  trainingMetrics: TrainingMetrics;
  recentReports: DashboardReportFeedItem[];
}

export interface ScoutDashboardStats {
  totalPlayers: number;
  averageRating: number;
  topPerformers: TopPerformer[];
  underPerformers: TopPerformer[];
  recentReports: DashboardReportFeedItem[];
  playerComparisons: TopPerformer[];
  performance: PerformanceMetrics;
}

export type StaffDashboardStats =
  | ManagerDashboardStats
  | CoachDashboardStats
  | MedicalDashboardStats
  | ScoutDashboardStats;

export interface DashboardReportFeedItem {
  id: string;
  type: DashboardReportType;
  playerId: string;
  playerName: string;
  authorId: string;
  authorName: string;
  date: string;
}

export interface TopPerformer {
  id: string;
  name: string;
  rating: number;
}

export interface DashboardInjury {
  playerId: string;
  playerName: string;
  status: string;
}

export interface TeamHealthMetrics {
  fitnessAverage: number;
  injuryRate: number;
  recoveryPlayers: number;
  questionablePlayers: number;
  trainingAttendance: number;
}

export interface PerformanceMetrics {
  averageRating: number;
  ratingTrend: 'improving' | 'declining' | 'stable';
  underPerformers: TopPerformer[];
  topScorers: TopPerformer[];
  topAssists: TopPerformer[];
  formPlayers: TopPerformer[];
}

export interface UpcomingFixture {
  id: string;
  opponent: string;
  competition: string;
  venue: 'home' | 'away' | 'neutral';
  kickoffAt: string;
  daysUntil: number;
  status: string;
}

export interface Alert {
  id: string;
  type: 'injury' | 'performance' | 'training' | 'fixture';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  playerId?: string;
  date: string;
}

export interface TrainingMetrics {
  weeklyAttendance: number;
  averageFitness: number;
  attitudeScore: number;
  lateArrivals: number;
  totalSessions: number;
}
